import { action, computed, makeObservable } from 'mobx';
import { I18nModel } from 'src/context/i18n/I18nModel';
import { CompetitionGame } from 'src/entities/competition/domain';
import { DateFieldModel } from 'src/shared/ui/inputs/datetime/DateFieldModel';
import { TimePickerFieldModel } from 'src/shared/ui/inputs/datetime/TimePickerFieldModel';
import { TEXT_FIELD_TYPE, TextFieldModel } from 'src/shared/ui/inputs/text/TextFieldModel';

interface EditGameDataModalProps {
  i18n: I18nModel;
  game: CompetitionGame;
}

interface EditGameData {
  date: Date;
  courtNumber: number;
  gameId: number;
  competitionId: number;
  activity_name?: string;
}

export class EditGameDataModel {
  readonly date: DateFieldModel;
  readonly time: TimePickerFieldModel;
  readonly court: TextFieldModel;
  readonly activityName?: TextFieldModel;
  game: CompetitionGame;

  constructor(props: EditGameDataModalProps) {
    this.game = props.game;
    this.date = new DateFieldModel({
      i18n: props.i18n,
      title: 'Date',
      required: true,
      id: 'date',
      initialValue: props.game.date,
    });
    this.time = new TimePickerFieldModel({
      i18n: props.i18n,
      title: 'Time',
      required: true,
      id: 'time',
      initialValue: '',
    });
    this.time.setDefaultValueFromDate(props.game.date);
    this.court = new TextFieldModel({
      i18n: props.i18n,
      title: 'Court',
      required: false,
      id: 'court',
      type: TEXT_FIELD_TYPE.number,
      initialValue: props.game.court ? props.game.court.toString() : '',
    });

    // Add activity name field only if this is an activity
    if (props.game.activityFlag) {
      this.activityName = new TextFieldModel({
        i18n: props.i18n,
        title: 'Activity name',
        required: true,
        id: 'activityName',
        type: TEXT_FIELD_TYPE.text,
        initialValue: props.game.activityName || '',
      });
    }

    makeObservable(this, {
      hasChanged: computed,
      hasError: computed,
      data: computed,
      reset: action.bound,
    });
  }

  get data(): EditGameData {
    const date = this.date.value || new Date();
    const startTime = this.time.valueAsNumbers;
    if (startTime) {
      date.setHours(startTime.hours);
      date.setMinutes(startTime.minutes);
    }

    const gameData: EditGameData = {
      date,
      courtNumber: Number(this.court.value),
      gameId: this.game.id,
      competitionId: this.game.competitionId,
    };

    // Include activity_name in payload only for activities
    if (this.game.activityFlag && this.activityName) {
      gameData.activity_name = this.activityName.value;
    }

    return gameData;
  }

  get hasChanged(): boolean {
    return (
      this.date.hasChanged ||
      this.time.hasChanged ||
      this.court.hasChanged ||
      (this.activityName ? this.activityName.hasChanged : false)
    );
  }

  get hasError(): boolean {
    return (
      this.date.hasError ||
      this.time.hasError ||
      this.court.hasError ||
      (this.activityName ? this.activityName.hasError : false)
    );
  }

  reset(): void {
    this.date.reset();
    this.time.reset();
    this.court.reset();
    if (this.activityName) {
      this.activityName.reset();
    }
  }
}
